import React, { useCallback, useRef } from "react";
import moment from "moment";
import ZForm from "@organisms/ZForm";
import {
	EWidgets,
	ICustomFormItemProps,
	IFormFieldProps,
	IFormMetaProps,
	IFormProps,
	IFormSectionProps
} from "@organisms/ZForm/interface";
import { IMeetingUIProps, IRelationOption } from "../interface";
import {
	MeetingFormValues,
	MeetingTimeDateRange,
	SalesActivityAttendee,
	SalesActivityReminderUnit,
	SalesActivityUserType
} from "@/Contracts/Others/SalesActivity";
import {
	cache,
	VendorUserData,
	ZomentumEntities,
	ZomentumRoutes,
	ZomentumWindow
} from "@/Contracts";
import FormBuilder from "antd-form-builder";
import { EColors } from "@atoms/ZColors/interface";
import { EIcons } from "@atoms/ZIcon/interface";
import { getNearestHalfHour, isVendorApp } from "@/Utils";
import ZIcon from "@atoms/ZIcon";
import ZTooltip from "@atoms/ZTooltip";
import ZTypography from "@atoms/ZTypography";
import {
	capitalizeFirstLetter,
	filterOptionsOnSearch,
	TinyMceOptions
} from "@/Contracts/Utils";
import { EInputNumberWidths } from "@atoms/ZInputNumber/interface";
import range from "lodash/range";
import Row from "antd/lib/row";
import Col from "antd/lib/col";
import {
	IOptionGroupProps,
	IOptionProps
} from "@atoms/ZSelect/interface";
import ZCheckbox from "@atoms/ZCheckbox";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import "../index.less";
import { UserData } from "@/Contracts/Users";
import { ClientContact } from "@/Contracts/Clients";
import { Editor } from "@tinymce/tinymce-react";
import { Form } from "antd";
// import { useLocation } from "react-router-dom";
import { useMobileView } from "@/Hooks";
import { TimePickerType } from "./interface";
import { useState } from "react";
import { MeetingFormValuesCacheKey } from "../../../../pages/Drawers/SalesActivities/interface";
import ConflictMeeting from "./ConflictMeeting";
import { useRouteMatch } from "react-router-dom";

declare let window: ZomentumWindow;

export const ZAddMeeting: React.FC<IMeetingUIProps> = ({
	onSubmit,
	onClose,
	form,
	isLoading = false,
	ZOpportunitySelectFormItem,
	ZClientSelectFormItem,
	userId = null,
	ZUserSelectFormItem,
	fetchMeetingClashStatus,
	isFetchingData,
	showClashingMeetingMessage,
	isSubmitDisabled = false,
	handleAttendeesChange,
	isAttendeesLoading,
	clientContactsSearchResult = [],
	companyUserList = [],
	attendees = [],
	addReminder,
	deleteReminder,
	reminders = [],
	meetingId,
	ZVendorPartnerSelectFormItem,
	ZVendorOpportunitySelectFormItem,
	ZVendorClientSelectFormItem,
	partnerContacts = [],
	datePattern,
	handleFormChange,
	openAddClientContactDrawer,
	setSelectedClient
}) => {
	const isVendorApplication = isVendorApp();
	const isVendorPartnersDetailPage =
		!!useRouteMatch(ZomentumRoutes.VendorPartnersDetail)?.isExact &&
		isVendorApplication;
	const startDate = useRef(moment(new Date()));
	const endDate = useRef(moment(new Date()).add(30, "minutes"));
	// const location = useLocation();

	const isMobileView = useMobileView();
	const [isConflictModalVisible, setIsConflictModalVisible] =
		useState<boolean>(false);

	const currentDate = new Date();
	const todayStartHour = currentDate.getHours();
	const todayStartMinute = currentDate.getMinutes();
	const currentStartDate = new Date(new Date().setHours(0, 0, 0, 0));
	const todayStartDateUnixTimeStamp = currentStartDate.getTime();

	const formatAttendeesOption = (
		attendee: UserData | VendorUserData | ClientContact
	): string => {
		return `${capitalizeFirstLetter(
			attendee.name ? attendee.name.full : ""
		)} <${attendee.email ? attendee.email : ""}>`;
	};

	const getRelationOptions = useCallback(() => {
		const relationOptions: IRelationOption[] = [];

		if (isVendorApplication) {
			relationOptions.push({
				label: "Opportunity",
				value: ZomentumEntities.VendorOpportunity,
				datatestid: "opportunity-radio",
				children: (
					<div className="mt-3 sales-activity-relation-options">
						<ZVendorOpportunitySelectFormItem />
					</div>
				)
			});
			relationOptions.push({
				label: "Client",
				value: ZomentumEntities.VendorClient,
				datatestid: "opportunity-radio",
				children: (
					<div className="mt-3 sales-activity-relation-options">
						<ZVendorClientSelectFormItem />
					</div>
				)
			});
			relationOptions.push({
				label: "Partner",
				value: ZomentumEntities.VendorCompany,
				children: (
					<div className="mt-3 sales-activity-relation-options">
						<ZVendorPartnerSelectFormItem />
					</div>
				)
			});
		} else {
			relationOptions.push({
				label: "Opportunity",
				value: ZomentumEntities.Opportunity,
				datatestid: "opportunity-radio",
				children: (
					<div className="mt-3 sales-activity-relation-options">
						<ZOpportunitySelectFormItem />
					</div>
				)
			});
			relationOptions.push({
				label: "Client",
				value: ZomentumEntities.Client,
				children: (
					<div className="mt-3 sales-activity-relation-options">
						<ZClientSelectFormItem />
					</div>
				)
			});
		}
		return relationOptions;
	}, [
		isVendorApplication,
		ZVendorClientSelectFormItem,
		ZVendorPartnerSelectFormItem,
		ZVendorOpportunitySelectFormItem,
		ZOpportunitySelectFormItem,
		ZClientSelectFormItem
	]);

	const getMeetingDateTimes = (): MeetingTimeDateRange => {
		return form.getFieldsValue([
			"start_time",
			"start_date",
			"end_time",
			"end_date"
		]) as MeetingTimeDateRange;
	};

	const setStartDate = (
		end_date: moment.Moment | null,
		_dateString: string
	) => {
		let { start_date, start_time } = getMeetingDateTimes();
		const { end_time } = getMeetingDateTimes();
		if (start_date && end_date) {
			if (start_date.unix() > end_date.unix()) {
				start_date = moment(end_date).subtract(30, "minutes");
				if (end_time) {
					start_time = moment(end_time).subtract(30, "minutes");
				} else {
					start_time = null;
				}
			}
		}
		if (!start_date && end_date) {
			start_date = moment(end_date).subtract(30, "minutes");
		}
		fetchMeetingClashStatus(
			{ start_date, start_time, end_date, end_time },
			meetingId
		);
	};

	const setEndDate = (
		start_date: moment.Moment | null,
		_dateString: string
	) => {
		let { end_time, end_date } = getMeetingDateTimes();
		const { start_time } = getMeetingDateTimes();
		if (start_date && end_date) {
			if (start_date.unix() > end_date.unix()) {
				end_date = moment(start_date).add(30, "minutes");
				if (start_time) {
					end_time = moment(start_time).add(30, "minutes");
				} else {
					end_time = null;
				}
			}
		}
		if (start_date && !end_date) {
			end_date = moment(start_date).add(30, "minutes");
		}
		fetchMeetingClashStatus(
			{ start_date, start_time, end_date, end_time },
			meetingId
		);
	};

	const setStartTime = (
		end_time: moment.Moment | null,
		_timeString: string
	) => {
		const { start_date, end_date } = getMeetingDateTimes();
		let { start_time } = getMeetingDateTimes();
		if (start_time && end_time) {
			if (start_time.unix() > end_time.unix()) {
				start_time = moment(end_time).subtract(30, "minutes");
			}
		}
		fetchMeetingClashStatus(
			{ start_date, start_time, end_date, end_time },
			meetingId
		);
	};

	const setEndTime = (
		start_time: moment.Moment | null,
		_timeString: string
	) => {
		const { start_date, end_date } = getMeetingDateTimes();
		let { end_time } = getMeetingDateTimes();
		if (start_time && end_time) {
			if (start_time.unix() > end_time.unix()) {
				end_time = moment(start_time).add(30, "minutes");
			}
		}
		fetchMeetingClashStatus(
			{ start_date, start_time, end_date, end_time },
			meetingId
		);
	};
	const getTimePickerDetail = (
		date_type: string,
		time_type: string,
		selectedHour?: number,
		selectedMinute?: number
	) => {
		const date = form.getFieldValue(date_type) as moment.Moment;
		if (date && date.unix() * 1000 >= todayStartDateUnixTimeStamp) {
			if (time_type === TimePickerType.Hour) {
				false;
			}
			if (
				time_type === TimePickerType.Minute &&
				selectedHour &&
				selectedHour >= todayStartHour
			) {
				false;
			}
			if (
				time_type === TimePickerType.Second &&
				selectedHour &&
				selectedMinute &&
				selectedHour >= todayStartHour &&
				selectedMinute >= todayStartMinute
			) {
				false;
			}
			return new Array<number>();
		}
		return new Array<number>();
	};

	const StartDateAndTime = () => (
		<FormBuilder
			form={form}
			disabled={isLoading || isFetchingData}
			meta={{
				columns: 2,
				fields: [
					{
						key: "start_date",
						required: true,
						message: `Please enter start date`,
						widget: EWidgets.DATEPICKER,
						wrapperCol: { span: 15 },
						widgetProps: {
							datePattern,
							allowClear: false,
							className: "w-full",
							placeholder: `Start Date`,
							onChange: setEndDate,
							disabledDate: false
						},
						hasFeedback: true,
						initialValue: moment(startDate.current)
					},
					{
						key: "start_time",
						widget: EWidgets.TIMEPICKER,
						wrapperCol: { span: 13 },
						widgetProps: {
							minuteStep: 5,
							allowClear: false,
							className: "w-full",
							onChange: setEndTime,
							format: "HH:mm",
							placeholder: `Start Time`,
							disabledHours: () =>
								getTimePickerDetail(
									"start_date",
									TimePickerType.Hour
								),
							disabledMinutes: (selectedHour: number) =>
								getTimePickerDetail(
									"start_date",
									TimePickerType.Minute,
									selectedHour
								),
							disabledSeconds: (
								selectedHour: number,
								selectedMinute: number
							) =>
								getTimePickerDetail(
									"start_date",
									TimePickerType.Second,
									selectedHour,
									selectedMinute
								)
						},
						hasFeedback: true,
						initialValue: getNearestHalfHour(startDate.current),
						required: true,
						message: `Please enter start time`
					}
				]
			}}
		/>
	);

	const EndDateAndTime = () => (
		<FormBuilder
			form={form}
			disabled={isLoading || isFetchingData}
			meta={{
				columns: 2,
				fields: [
					{
						key: "end_date",
						required: true,
						message: `Please enter end date`,
						widget: EWidgets.DATEPICKER,
						wrapperCol: { span: 15 },
						widgetProps: {
							datePattern,
							allowClear: false,
							className: "w-full",
							placeholder: `End Date`,
							onChange: setStartDate,
							disabledDate: false
						},
						hasFeedback: true,
						initialValue: moment(endDate.current)
					},
					{
						key: "end_time",
						widget: EWidgets.TIMEPICKER,
						wrapperCol: { span: 13 },
						widgetProps: {
							minuteStep: 5,
							allowClear: false,
							className: "w-full",
							onChange: setStartTime,
							format: "HH:mm",
							placeholder: `End Time`,
							disabledHours: () =>
								getTimePickerDetail(
									"end_date",
									TimePickerType.Hour
								),
							disabledMinutes: (selectedHour: number) =>
								getTimePickerDetail(
									"end_date",
									TimePickerType.Minute,
									selectedHour
								),
							disabledSeconds: (
								selectedHour: number,
								selectedMinute: number
							) =>
								getTimePickerDetail(
									"end_date",
									TimePickerType.Second,
									selectedHour,
									selectedMinute
								)
						},
						hasFeedback: true,
						initialValue: getNearestHalfHour(endDate.current),
						required: true,
						message: `Please enter end time`
					}
				]
			}}
		/>
	);

	const Reminder = (index: number) => (
		<FormBuilder
			form={form}
			disabled={isLoading}
			meta={{
				columns: 3,
				fields: [
					{
						key: `reminders.${index}`,
						widget: EWidgets.INPUTNUMBERWITHSELECT,
						colSpan: isMobileView ? 2 : 1,
						wrapperCol: { span: 24 },
						initialValue: reminders[index] ?? {
							number: 15 * (index + 1),
							selection: SalesActivityReminderUnit.Minute
						},
						rules: [
							() => ({
								validator(_: any, value: any) {
									if (
										form.getFieldValue(
											"reminder_enabled"
										) &&
										!value.number
									) {
										return Promise.reject(
											new Error(
												"Please enter reminder interval"
											)
										);
									}
									return Promise.resolve();
								}
							})
						],
						widgetProps: {
							width: EInputNumberWidths.lg,
							selectOptions: [
								{
									name: "Minutes",
									value: SalesActivityReminderUnit.Minute
								},
								{
									name: "Hours",
									value: SalesActivityReminderUnit.Hour
								},
								{
									name: "Days",
									value: SalesActivityReminderUnit.Day
								}
							]
						}
					},
					{
						key: `reminder_action.${index}`,
						render: () => {
							if (index === 0 && reminders.length === 1) {
								return (
									<ZTooltip
										title="Add Reminder"
										placement="right"
									>
										<span>
											<ZIcon
												name={EIcons.ADD}
												onClick={addReminder}
												className="cursor-pointer mt-1 ml-n5"
												size="sm"
												colorVariant={7}
											/>
										</span>
									</ZTooltip>
								);
							} else if (
								reminders.length > 1 &&
								index === reminders.length - 1
							) {
								return (
									<ZTooltip
										title="Delete Reminder"
										placement="right"
									>
										<span>
											<ZIcon
												name={EIcons.TRASH}
												colorVariant={7}
												onClick={deleteReminder}
												className="cursor-pointer mt-1 ml-n5"
												size="sm"
											/>
										</span>
									</ZTooltip>
								);
							} else {
								return null;
							}
						}
					}
				]
			}}
		/>
	);

	const optionsGroup: Array<IOptionGroupProps> = [
		{
			label: "Users",
			selectOptions: companyUserList.map(companyUser => ({
				key: companyUser.id,
				value: JSON.stringify(
					new SalesActivityAttendee(
						companyUser.id,
						isVendorApplication
							? SalesActivityUserType.VendorUser
							: SalesActivityUserType.User
					)
				),
				disabled: companyUser.is_disabled,
				name: companyUser.is_disabled
					? `${formatAttendeesOption(companyUser)} ( Inactive user )`
					: formatAttendeesOption(companyUser)
			}))
		}
	];

	if (clientContactsSearchResult && clientContactsSearchResult.length > 0) {
		optionsGroup.unshift({
			label: "Contacts",
			selectOptions: clientContactsSearchResult?.map(clientContact => ({
				key: clientContact.id,
				value: JSON.stringify(
					new SalesActivityAttendee(
						clientContact.id,
						isVendorApp()
							? SalesActivityUserType.VendorClientUser
							: SalesActivityUserType.ClientUser
					)
				),
				name: formatAttendeesOption(clientContact)
			}))
		});
	}

	if (isVendorPartnersDetailPage && !!partnerContacts?.length) {
		optionsGroup.unshift({
			label: "Partner Contacts",
			selectOptions: partnerContacts.map(partnerContact => ({
				key: partnerContact.id,
				value: JSON.stringify(
					new SalesActivityAttendee(
						partnerContact.id,
						SalesActivityUserType.User
					)
				),
				name: capitalizeFirstLetter(partnerContact.name ?? "")
			}))
		});
	}

	const nonContactUsers = attendees?.filter(attendee => {
		return attendee.attendee_type === SalesActivityUserType.Unknown;
	});

	if (nonContactUsers && nonContactUsers.length > 0) {
		optionsGroup.push({
			label: "Non Contact Users",
			selectOptions: nonContactUsers.map(attendee => ({
				key: attendee.attendee_id || Math.random().toString(),
				value: JSON.stringify(
					new SalesActivityAttendee(
						attendee.attendee_id,
						attendee.attendee_type,
						attendee.email
					)
				),
				name: attendee.email || ""
			}))
		});
	}

	const DescriptionEditor = () => (
		<Editor
			apiKey={process.env.REACT_APP_TINY_MCE}
			init={{
				...TinyMceOptions,
				toolbar: [],
				plugins: "",
				width: "100%",
				height: "144px",
				autoresize: false,
				placeholder: "Please enter description",
				content_style: `
					html{
						height: auto;
					}
					p{
						margin: 0;
					}
					.mce-content-body {
						font-family: "Inter";
						font-size: 14px;
						color: #262626;
						overflow-y: scroll;
						padding: 8px;
						margin:0;
						font-weight: 300;
					}
					.mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before{
						left: auto !important;
						color: #c1c1c1;
					}
				`,
				init_instance_callback: (editor: any) => {
					if (!!form.getFieldValue("description")?.length) {
						editor.setContent(form.getFieldValue("description"));
					}
				}
			}}
			disabled={false}
			id={"client-meetings-description-editor"}
			onEditorChange={() => {
				const editor = window.tinyMCE.get(
					"client-meetings-description-editor"
				);
				if (editor) {
					form.setFieldsValue({ description: editor.getContent() });
				}
			}}
		/>
	);

	const getRelatedToInitialValue = () => {
		if (isVendorApplication) {
			return window.location.pathname.includes(
				ZomentumRoutes.VendorOpportunityList
			)
				? ZomentumEntities.VendorOpportunity
				: window.location.pathname.includes(
						ZomentumRoutes.VendorClientList
				  )
				? ZomentumEntities.VendorClient
				: ZomentumEntities.VendorCompany;
		} else {
			return window.location.pathname.includes(
				ZomentumRoutes.OpportunityList
			)
				? ZomentumEntities.Opportunity
				: ZomentumEntities.Client;
		}
	};

	const handleConflictMeetingModal = () => {
		setIsConflictModalVisible(!isConflictModalVisible);
	};

	const fields: IFormFieldProps[] = [
		{
			key: "title",
			label: "Title",
			widget: EWidgets.INPUT,
			widgetProps: {
				width: "full",
				placeholder: "Please enter title"
			},
			required: true,
			message: "Please enter title",
			hasFeedback: true,
			rules: [
				{
					whitespace: true,
					message: "Title cannot be empty"
				}
			]
		},
		{
			key: "description",
			label: "Description",
			render: () => {
				return (
					<Form.Item label="Description" labelCol={{ span: 5 }}>
						<div className="mb-5 description-editor">
							<DescriptionEditor />
						</div>
					</Form.Item>
				);
			}
		},
		{
			key: "custom_start_date",
			label: "Start Date",
			required: true,
			initialValue: moment(),
			widget: StartDateAndTime
		},
		{
			key: "custom_end_date",
			label: "End Date",
			required: true,
			initialValue: moment(),
			widget: EndDateAndTime
		},
		{
			key: "availability",
			render: () => (
				<>
					{isFetchingData && (
						<Row className="flex items-center mb-6">
							<Col offset={5}>
								<ZIcon
									name={EIcons.RESEND}
									size="sm"
									className="ml-0 mr-2 mb-1"
								/>
								<ZTypography.ZText>
									Checking your availability
								</ZTypography.ZText>
							</Col>
						</Row>
					)}
					{showClashingMeetingMessage && (
						<Row className="flex items-center mb-6">
							<Col
								offset={isMobileView ? 0 : 5}
								className="meeting-conflict-col"
							>
								<ZIcon
									name={EIcons.WARNINGCIRCLE}
									size="sm"
									fillstrokesame={false}
									fillColor={EColors.YELLOW}
									fillColorVariant={6}
									fill
									className="m-0 mr-2 mb-1"
								/>
								<ZTypography.ZText>
									This meeting conflicts with&nbsp;
									<ZTypography.ZText
										color={EColors.BLUE}
										colorVariant={6}
										onClick={handleConflictMeetingModal}
										className="cursor-pointer"
									>
										other meetings&nbsp;
									</ZTypography.ZText>
									in your calendar.
								</ZTypography.ZText>
							</Col>
						</Row>
					)}
					{!isFetchingData && !showClashingMeetingMessage && (
						<Row className="flex items-center mb-6">
							<Col offset={isMobileView ? 0 : 5}>
								<ZIcon
									name={EIcons.CHECKCIRCLE}
									size="sm"
									fillstrokesame={false}
									fillColor={EColors.GREEN}
									fillColorVariant={7}
									fill
									className="m-0 mr-2 mb-1"
								/>
								<ZTypography.ZText>
									This meeting does not conflict with other
									meetings on your calendar
								</ZTypography.ZText>
							</Col>
						</Row>
					)}
				</>
			)
		},
		{
			key: "notes",
			label: "Notes",
			widget: EWidgets.INPUTTEXTAREA,
			widgetProps: {
				placeholder: "Please enter notes",
				rows: 6,
				allowClear: true
			}
		},
		{
			key: "relation",
			label: "Related to",
			widget: EWidgets.RADIOGROUP,
			initialValue: getRelatedToInitialValue(),
			widgetProps: {
				className: "sales-activity-relation-radio",
				space: "middle",
				options: getRelationOptions()
			},
			required: true,
			message: "Please select relation"
		},
		{
			key: "attendees",
			label: "Attendees",
			initialValue: [
				JSON.stringify(
					new SalesActivityAttendee(
						userId,
						isVendorApplication
							? SalesActivityUserType.VendorUser
							: SalesActivityUserType.User
					)
				)
			],
			widget: EWidgets.SELECT,
			widgetProps: {
				onChange: handleAttendeesChange,
				showSearch: true,
				allowClear: true,
				loading: isAttendeesLoading,
				placeholder: "Please select attendees",
				filterOption: filterOptionsOnSearch,
				mode: "tags",
				isOptionsGroup: true,
				optionsGroup,
				dropdownRender: (menu: IOptionProps) => {
					return (
						<div>
							{menu}
							{!isVendorPartnersDetailPage && (
								<div
									className="addClientContact"
									onClick={openAddClientContactDrawer}
								>
									<ZTypography.ZText
										color={EColors.BLUE}
										colorVariant={6}
									>
										{isVendorApp()
											? "Add client contact"
											: "Add contact"}
									</ZTypography.ZText>
								</div>
							)}
						</div>
					);
				}
			}
		},
		{
			key: "author_id",
			render: () => <ZUserSelectFormItem />
		},
		{
			key: "location",
			label: "Location",
			widget: EWidgets.INPUT,
			widgetProps: {
				width: "full",
				placeholder: "Please enter location"
			}
		},
		{
			key: "reminder_enabled",
			widget: ({ value, onChange }: ICustomFormItemProps) => (
				<ZCheckbox
					checked={value}
					onChange={(e: CheckboxChangeEvent) =>
						onChange(e.target.checked)
					}
				>
					Send Email Reminder to attendees before
				</ZCheckbox>
			)
		}
	];

	const sections: Array<IFormSectionProps> = [
		{
			isIndentFormItems: false,
			getMeta: () => {
				const meta: IFormMetaProps = {
					formItemLayout: [5, 19],
					fields
				};

				const isReminderIntervalFieldExists = fields.find(
					field => field.key === "reminder_interval_[0]"
				);

				if (
					form.getFieldValue("reminder_enabled") &&
					!isReminderIntervalFieldExists
				) {
					reminders.length > 0 &&
						range(0, reminders.length).forEach(index => {
							meta.fields.push({
								key: `reminder_interval_[${index}]`,
								render: () => Reminder(index)
							});
						});
				}

				return meta;
			}
		}
	];

	const handleSubmit = (values: MeetingFormValues) => {
		const editor = window.tinyMCE.get("client-meetings-description-editor");
		if (editor) {
			values = {
				...values,
				description: editor.getContent().replace(/\n/g, "")
			};
		}
		setSelectedClient?.(null);
		onSubmit(values);
	};

	const handleOnDrawerClose = () => {
		cache.deleteItem(MeetingFormValuesCacheKey);
		onClose?.();
	};

	const formProps: IFormProps = {
		key: "AddMeetingForm",
		form,
		sections,
		disabled: isLoading,
		onFinish: handleSubmit,
		submitButtonText: "Save",
		isRightFooter: true,
		isStickFooter: true,
		isCancelAllowed: true,
		onCancel: handleOnDrawerClose,
		isSubmitDisabled,
		submitButtonProps: { "data-testid": "save" },
		isDynamic: true,
		shouldDisableBeforeTouch: false,
		shouldResetFormOnCancel: false,
		onValuesChange: handleFormChange
	};

	return (
		<>
			<ZForm {...formProps} className="py-4" />
			<ConflictMeeting
				isConflictModalVisible={isConflictModalVisible}
				onCancel={handleConflictMeetingModal}
				form={form}
			/>
		</>
	);
};

export default ZAddMeeting;
